import React, { useState, useEffect } from 'react';
import { X, FileText, AlertCircle } from 'lucide-react';
import { AccountMatch } from '../../types/account';
import { accountService } from '../../services';
import { formatDate } from '../../utils/dateUtils';

interface AccountMatchesModalProps {
  accountId: number;
  accountName: string;
  onClose: () => void;
}

export const AccountMatchesModal: React.FC<AccountMatchesModalProps> = ({
  accountId,
  accountName,
  onClose
}) => {
  const [matches, setMatches] = useState<AccountMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMatches();
  }, [accountId]);

  const loadMatches = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await accountService.getAccountMatches(accountId);
      setMatches(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load account matches');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Account Match Strings</h3>
            <p className="text-sm text-gray-600 mt-1">{accountName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-16 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <span className="text-red-800">{error}</span>
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No match strings found for this account</p>
              <p className="text-sm text-gray-400 mt-1">
                Match strings are created when CSV data is processed
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {matches.map((match, index) => (
                <div
                  key={match.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-mono text-gray-900 break-all">
                            {match.match_string}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 ml-8">
                        Added {formatDate(match.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">
            {matches.length} match {matches.length === 1 ? 'string' : 'strings'} found
          </p>
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
